/**
 * Shared hook for the employee's unpaid-leave usage (days applied this year).
 *
 * Unpaid leave has no entitlement/balance, so it can't render as an
 * "of N left" tile — this feeds the MY LEAVE grid's unpaid usage tile
 * instead. Counts pending as well as approved (days applied for).
 *
 * Endpoints (impersonation-aware, mirroring listMyBalances / listEmployeeBalances):
 *   - self:         GET /api/v1/me/unpaid-usage
 *   - "Viewing as": GET /api/v1/employees/{id}/unpaid-usage
 * Both respond: { year: number; daysApplied: number }
 *
 * The `/me` endpoint resolves the real actor (current_actor), so under
 * "Viewing as" it would return the HR viewer's own usage — same reason the
 * balance tiles beside this one switch to the employee-scoped endpoint.
 */
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { apiRequest } from './client'

// ── Zod schema (Zod at every boundary per CLAUDE.md) ─────────────────────────

// Pydantic serialises Decimal as a JSON string ("0", "3.0") — not a number.
// `z.coerce.number()` accepts both shapes (see me-replacement-cap.ts).
export const UnpaidUsageSchema = z.object({
  year: z.coerce.number(),
  daysApplied: z.coerce.number(),
})

export type UnpaidUsageData = z.infer<typeof UnpaidUsageSchema>

// ── Fetcher ───────────────────────────────────────────────────────────────────

async function fetchUnpaidUsage(employeeId?: string | null): Promise<UnpaidUsageData> {
  const path = employeeId
    ? `/api/v1/employees/${encodeURIComponent(employeeId)}/unpaid-usage`
    : '/api/v1/me/unpaid-usage'
  const raw = await apiRequest<unknown>(path)
  return UnpaidUsageSchema.parse(raw)
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export const UNPAID_USAGE_QUERY_KEY = ['me', 'unpaid-usage'] as const

export interface UseUnpaidUsageResult {
  data: UnpaidUsageData | undefined
  isPending: boolean
  isError: boolean
}

/**
 * TanStack Query hook returning the employee's unpaid days taken this year.
 *
 * @param employeeId  When set (HR "Viewing as"), reads that employee's usage
 *   via the employee-scoped endpoint. When null/undefined, reads the signed-in
 *   user's own usage via `/me`. The query key includes the id so switching the
 *   impersonation target refetches rather than serving a stale tile.
 *
 * - `staleTime: 5min` — usage doesn't change in real time.
 * - Always enabled: the home dashboard needs it on page load.
 */
export function useUnpaidUsage(employeeId?: string | null): UseUnpaidUsageResult {
  const { data, isPending, isError } = useQuery({
    queryKey: [...UNPAID_USAGE_QUERY_KEY, employeeId ?? 'self'],
    queryFn: () => fetchUnpaidUsage(employeeId),
    staleTime: 5 * 60_000,
  })
  return { data, isPending, isError }
}
